/**
 * @file: FieldRow.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * One labeled row inside the intake-details card: the field label on the left,
 * its value (or edit control) right-aligned. Rows above the first carry a
 * hairline top border; `alignTop` floats the label to the top for value cells
 * that wrap to multiple lines (the pill rows). `onPress` makes the whole row a
 * tap target (the advisor picker, the date toggle). `labelAccessory` renders an
 * inline affordance after the label (e.g. a "plate keyboard" link).
 */

import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TouchableOpacity, View } from 'react-native';

export const FieldRow = ({
  label,
  required,
  first,
  alignTop,
  onPress,
  labelAccessory,
  children,
}: {
  label: string;
  required?: boolean;
  first?: boolean;
  alignTop?: boolean;
  onPress?: () => void;
  labelAccessory?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { rpx } = useResponsiveContext();
  const rowStyle = {
    flexDirection: 'row',
    alignItems: alignTop ? 'flex-start' : 'center',
    minHeight: rpx(44),
    paddingVertical: rpx(10),
    borderTopWidth: first ? 0 : 1,
    borderTopColor: LD.hairline,
  } as const;

  const inner = (
    <>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: alignTop ? rpx(5) : 0,
          marginRight: rpx(12),
        }}
      >
        <Typography.Text size={rpx(14)} color={LD.steel} medium>
          {label}
        </Typography.Text>
        {required ? (
          <Typography.Text
            size={rpx(14)}
            color={LD.danger}
            style={{ marginLeft: rpx(3) }}
          >
            *
          </Typography.Text>
        ) : null}
        {labelAccessory}
      </View>
      <View style={{ flex: 1, alignItems: 'flex-end' }}>{children}</View>
    </>
  );

  return onPress ? (
    <TouchableOpacity activeOpacity={0.6} onPress={onPress} style={rowStyle}>
      {inner}
    </TouchableOpacity>
  ) : (
    <View style={rowStyle}>{inner}</View>
  );
};
